import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, Users, Lock, Star, Headphones, BookOpen } from "lucide-react";
import { api, getChildId } from "../lib/api";
import { loadRecordings, play, setAudioPrefs } from "../lib/audio";
import { ParentGate } from "../components/ParentGate";
import { Avatar } from "../components/Avatar";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { usePremiumPrompt, CrownBadge } from "../components/billing/PremiumPrompt";

const StoryTile = ({ s, k, color, onTap }) => {
  const locked = s.state === "locked" || s.state === "premium";
  return (
    <motion.button data-testid={`life-story-${s.story_id}`} onClick={() => onTap(s)} whileTap={{ scale: 0.95 }}
      className={`w-full flex items-center gap-3 rounded-2xl px-3 py-3 border-2 text-left ${locked ? "bg-stone-100 border-stone-200 text-stone-400" : "bg-white border-transparent shadow-sm hover:shadow-md"}`}>
      <span className="w-11 h-11 rounded-full flex items-center justify-center text-white text-xl shrink-0" style={{ background: locked ? "#c7c2b8" : color }}>
        {locked ? <Lock size={18} /> : k + 1}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-xl truncate">{s.story_title}</span>
        {s.state === "completed" && (
          <span className="flex" data-testid={`life-story-stars-${s.story_id}`}>{[1, 2, 3].map((i) => <Star key={i} size={16} className={i <= s.stars ? "text-amber-400" : "text-stone-300"} fill="currentColor" />)}</span>
        )}
      </span>
      <span className="text-2xl">{s.state === "premium" ? <CrownBadge /> : s.sticker}</span>
    </motion.button>
  );
};

const ThemeCard = ({ t, i, onTap }) => (
  <motion.section data-testid={`life-theme-${t.theme_id}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
    className="rounded-[2rem] overflow-hidden bg-white/70 border-4 shadow-md" style={{ borderColor: `${t.color}55` }}>
    <div className="relative h-28 overflow-hidden">
      <img src={t.background} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${t.color}ee 0%, ${t.color}88 55%, transparent 100%)` }} />
      <div className="absolute inset-0 flex items-center gap-3 px-5 text-white">
        <span className="text-5xl drop-shadow">{t.emoji}</span>
        <div>
          <div className="text-3xl drop-shadow">{t.name}</div>
          <div className="text-base opacity-90" data-testid={`life-theme-progress-${t.theme_id}`}>完成 {t.completed} / {t.stories.length}</div>
        </div>
      </div>
    </div>
    <div className="p-3 space-y-2" style={{ background: `${t.color}14` }}>
      {t.stories.map((s, k) => <StoryTile key={s.story_id} s={s} k={k} color={t.color} onTap={onTap} />)}
    </div>
  </motion.section>
);

export default function LifeHall() {
  const nav = useNavigate();
  const childId = getChildId();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [gate, setGate] = useState(false);
  useEffect(() => {
    if (!childId) return nav("/profiles");
    loadRecordings();
    api.get(`/children/${childId}/life`).then(({ data }) => { setData(data); setAudioPrefs(data.child.settings); }).catch(() => nav("/profiles"));
  }, [childId, nav]);
  const [prompt, premiumEl] = usePremiumPrompt();
  const tap = (s) => { if (s.state === "premium") return prompt(); play(s.story_title); if (s.state !== "locked") setSel(s); };
  if (!data) return <div className="min-h-screen life-bg" />;
  const { child } = data;
  return (
    <div className="min-h-screen life-bg font-round" data-testid="life-hall-page">
      <header className="sticky top-0 z-10 flex items-center gap-2 sm:gap-3 px-3 sm:px-8 py-3 bg-white/80 backdrop-blur-md border-b-2 border-[#cfe8e2]">
        <button data-testid="life-back-btn" onClick={() => nav("/halls")} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-white border-2 border-[#cfe8e2] flex items-center justify-center"><ArrowLeft /></button>
        <span className="text-4xl w-14 h-14 shrink-0 rounded-full bg-[#E9F6F1] border-4 border-[#7cc4b4] hidden sm:flex items-center justify-center overflow-hidden"><Avatar value={child.avatar} /></span>
        <div className="text-3xl text-[#2c6e65] hidden sm:block">生活中文館</div>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
          <div className="hidden sm:flex items-center gap-2 px-4 h-14 rounded-full bg-white border-2 border-amber-300 text-2xl text-amber-600" data-testid="life-star-count"><Star fill="currentColor" /> {child.stars}</div>
          <button data-testid="life-listen-btn" onClick={() => nav("/listen?hall=life")} className="h-14 px-4 sm:px-5 rounded-full bg-[#3E9C8F] text-white flex items-center gap-2 shadow-md text-lg"><Headphones size={24} /><span className="hidden sm:inline">只聽故事</span></button>
          <button data-testid="life-rewards-btn" onClick={() => nav("/rewards")} className="w-14 h-14 rounded-full bg-[#E8845C] text-white flex items-center justify-center shadow-md"><Award size={28} /></button>
          <button data-testid="life-parent-btn" onClick={() => setGate(true)} className="w-14 h-14 rounded-full bg-white border-2 border-stone-300 text-stone-500 flex items-center justify-center"><Users size={22} /></button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 pb-16">
        <h1 className="text-4xl sm:text-5xl text-[#2c6e65] mb-2">今天想學什麼呢？</h1>
        <p className="text-xl text-stone-500 mb-8">每個主題有三個生活小故事，一個完成就能打開下一個。</p>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {data.themes.map((t, i) => <ThemeCard key={t.theme_id} t={t} i={i} onTap={tap} />)}
        </div>
      </main>
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-3xl border-4 border-[#cfe8e2] font-round" data-testid="life-story-dialog">
          {sel && (
            <>
              <img src={sel.thumbnail} alt="" className="w-full h-44 object-cover" />
              <div className="p-6">
                <DialogTitle className="font-round font-normal text-4xl text-[#2c6e65] flex items-center gap-3">{sel.sticker} {sel.story_title}</DialogTitle>
                <p className="text-lg text-stone-500 mt-2">{sel.description_zh}</p>
                <div className="grid gap-3 mt-5">
                  <button data-testid="life-start-btn" onClick={() => nav(`/lesson/${sel.story_id}`)} className="kid-btn flex items-center justify-center gap-3 text-2xl bg-[#3E9C8F] text-white"><BookOpen /> 開始學習</button>
                  <button data-testid="life-story-listen-btn" onClick={() => nav(`/listen?hall=life&start=${sel.story_id}`)} className="kid-btn flex items-center justify-center gap-3 text-2xl bg-white border-2 border-[#cfe8e2] text-[#2c6e65]"><Headphones /> 只聽故事</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ParentGate open={gate} onOpenChange={setGate} onPass={() => nav("/parent")} />
      {premiumEl}
    </div>
  );
}
